import type { Text } from 'mdast';
import type { MdxJsxFlowElement } from 'mdast-util-mdx-jsx';
import React, { useMemo } from 'react';
import { nameToEmoji } from '../../../gemoji';
import { MarkdownIconRegisterMap } from '../../../MarkdownIcon/constants';
import type { IconComponentProps } from '../../../MarkdownIcon/typings';
import type { MdxExternalComponent } from '../type';
import './inlineIcon.css';

function createMarkdownIcon(FN: React.FC<IconComponentProps>) {
  function MdxIcon(props: { iconName: string }) {
    return <FN iconName={props.iconName} className={'markdown-inline-icon'} />;
  }
  MdxIcon.attributes = ['iconName'];
  MdxIcon.inline = true;

  return MdxIcon;
}

const MarkdownIconPrefixList = Object.keys(
  MarkdownIconRegisterMap,
) as (keyof typeof MarkdownIconRegisterMap)[];

// 之后增加只需要扩展这个map就可以增加 icon
const MarkdownIconComponentMap = MarkdownIconPrefixList.reduce((acc, item) => {
  acc[item] = createMarkdownIcon(MarkdownIconRegisterMap[item]);
  return acc;
}, {} as Record<keyof typeof MarkdownIconRegisterMap, ReturnType<typeof createMarkdownIcon>>);

function toCamelCase(str: string) {
  return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

function createMarkdownIconComponent(iconName: string) {
  return `Mdx${toCamelCase(iconName)}`;
}

function isMarkdownIconType(value: any): value is keyof typeof MarkdownIconComponentMap {
  return MarkdownIconPrefixList.includes(value);
}

const allIconPrefixRegex = new RegExp(`:(${MarkdownIconPrefixList.join('|')})-([-\\w]+):`, 'g');

function defenseIconInput(icon: string) {
  return icon.split(':')[1];
}
/**
 * 注入到 viewer 的 externalComponent
 */
export const MarkdownIconComponentForText = MarkdownIconPrefixList.reduce((acc, item) => {
  acc[createMarkdownIconComponent(item)] = MarkdownIconComponentMap[item] as MdxExternalComponent;
  return acc;
}, {} as Record<string, MdxExternalComponent>);

/**
 * 针对所有 文本中需要识别图标渲染的逻辑
 * @param template
 * @param handler
 * @returns
 */
export function SupportIconTextReplacer<R>(
  template: string,
  handler: (data: Text | MdxJsxFlowElement) => R,
) {
  const next = template.replace(/:(\+1|\-1|[-\w]+):/g, function (_, $1) {
    return Object.prototype.hasOwnProperty.call(nameToEmoji, $1) ? nameToEmoji[$1] : _;
  });

  const iconRes = Array.from(next.matchAll(allIconPrefixRegex));

  if (iconRes.length === 0) return next;

  const fakeNode = iconRes.reduce(
    (res, item, i) => {
      const before = next.substring(res.cur, item.index);
      const full = item[0];
      const iconType = item[1];
      const iconName = item[2];
      const after = next.substring(item.index! + full.length);
      res.result.push(
        handler({
          type: 'text',
          value: before,
        }),
      );
      res.cur = item.index! + full.length;
      if (isMarkdownIconType(iconType)) {
        // console.log(
        //   iconType,
        //   iconName,
        //   createMarkdownIconComponent(iconType),
        //   MarkdownIconComponentForText[createMarkdownIconComponent(iconType)],
        // );
        res.result.push(
          handler({
            type: 'mdxJsxFlowElement',
            name: createMarkdownIconComponent(iconType),
            attributes: [
              {
                type: 'mdxJsxAttribute',
                name: 'iconName',
                value: iconName,
              },
            ],
            children: [],
          }),
        );
      }

      // todo fill icon element
      if (i === iconRes.length - 1) {
        res.result.push(
          handler({
            type: 'text',
            value: after,
          }),
        );
      }
      return res;
    },
    {
      cur: 0,
      result: [] as R[],
    },
  );

  return fakeNode.result;
}

export function useInnerIconPropsNode(icon: string) {
  // 处理为不包含::的数据
  return useMemo(() => {
    const iconTemplate = (() => {
      if (typeof icon !== 'string') {
        return undefined;
      }
      if (
        icon.endsWith(':') &&
        MarkdownIconPrefixList.some((prefix) => icon.startsWith(`:${prefix}-`))
      ) {
        return defenseIconInput(icon);
      }
      if (MarkdownIconPrefixList.some((prefix) => icon.startsWith(prefix))) {
        return defenseIconInput(`:${icon}:`);
      }
      return undefined;
    })();
    if (iconTemplate === undefined) return null;
    let iconType = '';
    let iconName = '';
    for (let index = 0; index < MarkdownIconPrefixList.length; index++) {
      const prefix = MarkdownIconPrefixList[index];
      if (iconTemplate.startsWith(`${prefix}-`)) {
        iconType = prefix;
        iconName = iconTemplate.replace(`${prefix}-`, '');
        break;
      }
    }
    if (!isMarkdownIconType(iconType)) return null;
    return React.createElement(MarkdownIconComponentMap[iconType], { iconName });
  }, [icon]);
}

export function SupportIconTitleNode({ title = '' }: { title?: string }) {
  const tabNode = useMemo(() => {
    return SupportIconTextReplacer(title, (data) => {
      if (data.type === 'text') {
        return <>{data.value}</>;
      }
      if (data.type === 'mdxJsxFlowElement') {
        const ComponentFc = data.name ? MarkdownIconComponentForText[data.name] : null;
        if (!ComponentFc) return <></>;
        return (
          <ComponentFc
            {...data.attributes.reduce((res, i) => {
              if (i.type === 'mdxJsxAttribute' && typeof i.value === 'string') {
                res[i.name] = i.value;
              }
              return res;
            }, {} as Record<string, string>)}
          />
        );
      }
      return null;
    });
  }, [title]);

  return <>{tabNode}</>;
}
